import { helpers } from "../../util/helpers.ts";
import type { RatingKey } from "../../../../../common/types.basketball.ts";
import { gradientStyleFactory } from "../../util/gradientStyleFactory.ts";
import i18n from "../../util/i18n.ts";

const gradientStyle = gradientStyleFactory(25, 45, 55, 75);

const colTitle = (key: string, defaultValue: string) =>
	i18n.t(`title.${key}`, { ns: "cols", defaultValue });

type Props = {
	ratings?: {
		pos: string;
		ovr: number;
		pot: number;
	} & Record<RatingKey, number>;
	stats: any;
	type?: "career" | "current" | "draft" | number;
	challengeNoRatings: boolean;
};

const RatingsStats = ({ challengeNoRatings, ratings, stats, type }: Props) => {
	const seasonPrefix =
		typeof type === "number" ? `${type} ` : type === "career" ? "Peak " : "";
	const seasonPrefix2 =
		type === "career" || type === "draft" ? "Career " : seasonPrefix;

	let ratingsBlock;

	if (challengeNoRatings) {
		ratingsBlock = null;
	} else if (ratings) {
		ratingsBlock = (
			<div className="row mb-2">
				<div className="col-4">
					<b>{seasonPrefix}Ratings</b>
					<br />
					<span style={gradientStyle(ratings.hgt)}>
						{colTitle("rating_hgt", "Hgt")}: {ratings.hgt}
					</span>
					<br />
					<span style={gradientStyle(ratings.stre)}>
						{colTitle("rating_stre", "Str")}: {ratings.stre}
					</span>
					<br />
					<span style={gradientStyle(ratings.spd)}>
						{colTitle("rating_spd", "Spd")}: {ratings.spd}
					</span>
					<br />
					<span style={gradientStyle(ratings.jmp)}>
						{colTitle("rating_jmp", "Jmp")}: {ratings.jmp}
					</span>
					<br />
					<span style={gradientStyle(ratings.endu)}>
						{colTitle("rating_endu", "End")}: {ratings.endu}
					</span>
				</div>
				<div className="col-4">
					<span style={gradientStyle(ratings.ovr)}>
						{colTitle("Ovr", "Overall")}: {ratings.ovr}
					</span>
					<br />
					<span style={gradientStyle(ratings.ins)}>
						{colTitle("rating_ins", "Ins")}: {ratings.ins}
					</span>
					<br />
					<span style={gradientStyle(ratings.dnk)}>
						{colTitle("rating_dnk", "Dnk")}: {ratings.dnk}
					</span>
					<br />
					<span style={gradientStyle(ratings.ft)}>
						{colTitle("rating_ft", "FT")}: {ratings.ft}
					</span>
					<br />
					<span style={gradientStyle(ratings.fg)}>
						{colTitle("rating_fg", "2Pt")}: {ratings.fg}
					</span>
					<br />
					<span style={gradientStyle(ratings.tp)}>
						{colTitle("rating_tp", "3Pt")}: {ratings.tp}
					</span>
				</div>
				<div className="col-4">
					<span style={gradientStyle(ratings.pot)}>
						{colTitle("Pot", "Potential")}: {Math.round(ratings.pot)}
					</span>
					<br />
					<span style={gradientStyle(ratings.oiq)}>
						{colTitle("rating_oiq", "oIQ")}: {ratings.oiq}
					</span>
					<br />
					<span style={gradientStyle(ratings.diq)}>
						{colTitle("rating_diq", "dIQ")}: {ratings.diq}
					</span>
					<br />
					<span style={gradientStyle(ratings.drb)}>
						{colTitle("rating_drb", "Drb")}: {ratings.drb}
					</span>
					<br />
					<span style={gradientStyle(ratings.pss)}>
						{colTitle("rating_pss", "Pss")}: {ratings.pss}
					</span>
					<br />
					<span style={gradientStyle(ratings.reb)}>
						{colTitle("rating_reb", "Reb")}: {ratings.reb}
					</span>
				</div>
			</div>
		);
	} else {
		ratingsBlock = (
			<div className="row mb-2">
				<div className="col-12">
					<b>{seasonPrefix}Ratings</b>
					<br />
					<br />
					<br />
					<br />
					<br />
					<br />
					<br />
				</div>
			</div>
		);
	}

	let statsBlock;

	if (stats) {
		statsBlock = (
			<div className="row">
				<div className="col-4">
					<b>{seasonPrefix2}Stats</b>
					<br />
					{colTitle("stat_pts", "PTS")}: {helpers.roundStat(stats.pts, "pts")}
					<br />
					{colTitle("stat_trb", "TRB")}: {helpers.roundStat(stats.trb, "trb")}
					<br />
					{colTitle("stat_ast", "AST")}: {helpers.roundStat(stats.ast, "ast")}
					<br />
					{colTitle("stat_fgp", "FG%")}: {helpers.roundStat(stats.fgp, "fgp")}%
					<br />
					{colTitle("stat_tsp", "TS%")}: {helpers.roundStat(stats.tsp, "tsp")}%
				</div>
				<div className="col-4">
					<br />
					{colTitle("stat_blk", "BLK")}: {helpers.roundStat(stats.blk, "blk")}
					<br />
					{colTitle("stat_stl", "STL")}: {helpers.roundStat(stats.stl, "stl")}
					<br />
					{colTitle("stat_tov", "TOV")}: {helpers.roundStat(stats.tov, "tov")}
					<br />
					{colTitle("stat_tpp", "3P%")}: {helpers.roundStat(stats.tpp, "tpp")}%
					<br />
					{colTitle("stat_tpar", "3PAr")}: {helpers.roundStat(stats.tpar, "tpar")}
				</div>
				<div className="col-4">
					<br />
					{colTitle("stat_min", "MP")}: {helpers.roundStat(stats.min, "min")}
					<br />
					{colTitle("stat_per", "PER")}: {helpers.roundStat(stats.per, "per")}
					<br />
					{colTitle("stat_ewa", "EWA")}: {helpers.roundStat(stats.ewa, "ewa")}
					<br />
					{colTitle("stat_ftp", "FT%")}: {helpers.roundStat(stats.ftp, "ftp")}%
					<br />
					{colTitle("stat_ftr", "FTr")}: {helpers.roundStat(stats.ftr, "ftr")}
				</div>
			</div>
		);
	} else {
		statsBlock = (
			<div className="row mt-2">
				<div className="col-12">
					<b>{seasonPrefix2}Stats</b>
					<br />
					<br />
					<br />
					<br />
				</div>
			</div>
		);
	}

	return (
		<>
			{ratingsBlock}
			{statsBlock}
		</>
	);
};

export default RatingsStats;
